import { useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, resizeImage } from "@/components/ProfileDialog";
import { toast } from "sonner";
import { Camera, Trash2 } from "lucide-react";
import { invalidateUserDirectory } from "@/components/UserTag";

export default function EditUserDialog({ target, isSelf, onClose, onSaved }) {
  const fileRef = useRef(null);
  const [form, setForm] = useState({ name: "", email: "", role: "staff", password: "" });
  const [avatar, setAvatar] = useState(null);

  useEffect(() => {
    if (target) {
      setForm({ name: target.name, email: target.email, role: target.role, password: "" });
      setAvatar(target.avatar || null);
    }
  }, [target]);

  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f || !f.type.startsWith("image/")) return;
    try { setAvatar(await resizeImage(f)); } catch { toast.error("Gagal membaca gambar"); }
  };

  const save = async () => {
    try {
      const body = { name: form.name, email: form.email, role: form.role, avatar: avatar || "" };
      if (form.password) body.password = form.password;
      await api.put(`/users/${target.id}`, body);
      toast.success("Akun diperbarui");
      invalidateUserDirectory();
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="edit-user-dialog">
        <DialogHeader><DialogTitle>Edit Akun</DialogTitle><DialogDescription>Ubah data akun {target?.name}.</DialogDescription></DialogHeader>
        <div className="flex items-center gap-4">
          <Avatar user={{ name: form.name, avatar }} className="h-16 w-16 text-lg" />
          <div className="flex flex-col gap-2">
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pick} data-testid="edit-user-avatar-input" />
            <Button type="button" size="sm" variant="outline" onClick={() => fileRef.current?.click()} data-testid="edit-user-avatar-upload"><Camera className="h-4 w-4" />Ganti Foto</Button>
            {avatar && <Button type="button" size="sm" variant="ghost" className="text-rose-600" onClick={() => setAvatar(null)} data-testid="edit-user-avatar-remove"><Trash2 className="h-4 w-4" />Hapus Foto</Button>}
          </div>
        </div>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Nama</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="edit-user-name" /></div>
          <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="edit-user-email" /></div>
          <div className="space-y-1.5"><Label>Peran</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })} disabled={isSelf}>
              <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="staff">Staf</SelectItem><SelectItem value="accounting">Accounting</SelectItem><SelectItem value="owner">Owner</SelectItem></SelectContent>
            </Select>
            {isSelf && <p className="text-[11px] text-slate-400">Peran akun sendiri tidak dapat diubah.</p>}
          </div>
          <div className="space-y-1.5"><Label>Kata Sandi Baru (opsional)</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Kosongkan jika tidak diganti" data-testid="edit-user-password" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="edit-user-cancel">Batal</Button>
          <Button onClick={save} data-testid="edit-user-save">Simpan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
